import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { TrendingUp, TrendingDown, RefreshCw } from 'lucide-react';
import { BACKEND_URL } from '../config';

const BondsPage = () => {
  const [bonds, setBonds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  const fetchBonds = async (showLoading = false) => {
    try {
      if (showLoading) setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/bonds/prices`);
      const data = await response.json();
      if (Array.isArray(data)) {
        setBonds(data);
        setLastUpdate(new Date());
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching bonds:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBonds(true);
    const interval = setInterval(() => fetchBonds(false), 60000); // Update every minute
    return () => clearInterval(interval);
  }, []);

  const usBonds = bonds.filter(b => b.region === 'USA');
  const europeBonds = bonds.filter(b => b.region === 'Europe');

  const BondCard = ({ bond }) => (
    <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-xl font-bold text-gray-900">{bond.name}</h3>
          <p className="text-sm text-gray-500">{bond.symbol} • {bond.maturity}</p>
        </div>
        {bond.change >= 0 ? (
          <TrendingUp className="w-6 h-6 text-green-600" />
        ) : (
          <TrendingDown className="w-6 h-6 text-red-600" />
        )}
      </div>

      <div className="mb-4">
        <div className="text-4xl font-bold text-gray-900">
          {bond.yield.toFixed(2)}%
        </div>
        <div className="text-sm text-gray-500 mt-1">Yield</div>
        <div className={`text-lg font-semibold mt-2 ${
          bond.change >= 0 ? 'text-green-600' : 'text-red-600'
        }`}>
          {bond.change >= 0 ? '+' : ''}{bond.change.toFixed(2)}%
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="relative bg-gradient-to-br from-[#0d6571] to-[#0d6571]/80 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <h1 className="text-5xl font-bold text-white mb-4">
            Government Bonds
          </h1>
          <p className="text-xl text-white/90">
            US and European government bond yields
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Controls */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Live Government Bond Yields</h2>
              <p className="text-sm text-gray-600 mt-1">Real-time data from Yahoo Finance</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-sm text-gray-600">
                Last updated: {lastUpdate.toLocaleTimeString()}
              </div>
              <button
                onClick={() => fetchBonds(false)}
                className="flex items-center gap-2 px-4 py-2 bg-[#0d6571] text-white rounded-lg hover:bg-[#0d6571]/90 transition-colors"
                disabled={loading}
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-20">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#0d6571] mx-auto mb-4"></div>
            <p className="text-gray-600">Loading bond data...</p>
          </div>
        ) : (
          <>
        {/* US Bonds */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">United States Treasuries</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {usBonds.map((bond, idx) => (
              <BondCard key={idx} bond={bond} />
            ))}
          </div>
        </div>

        {/* Europe Bonds */}
        <div>
          <h2 className="text-3xl font-bold text-gray-900 mb-6">European Government Bonds</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {europeBonds.map((bond, idx) => (
              <BondCard key={idx} bond={bond} />
            ))}
          </div>
        </div>
        </>
        )}

        <div className="mt-8 bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Market Information</h2>
          <p className="text-gray-600">
            Real-time government bond yields provided by Yahoo Finance. Data updates automatically every minute. Click refresh for manual update.
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default BondsPage;